import {AbstractControl, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {FeedbackAnswerState, FeedbackQuestionType} from '../../../shared/types/generated';
import {
	FeedbackStructureQuestionFormQuestion,
	FeedbackStructureQuestionOption,
} from '../feedback-structure-create-update-dialog/feedback-structure-question/feedback-structure-question-form.model';
import {
	feedbackAnswerControlName,
	feedbackCommentControlName,
	feedbackOptionCommentControlName,
	feedbackOptionControlName,
} from '../helpers/feedback-structure-preview-control-names';
import {
	createAnswerRequest,
	FeedbackAttemptControlValueGetter,
	normalizedAnswerValue,
	hasQuestionCommentConfiguration,
	normalizeTextValue,
	questionCommentValue,
} from './feedback-attempt-answer-mapper';

export function createRequiredCommentValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null =>
		normalizeTextValue(control.value) === null ? {required: true} : null;
}

export function updateCommentValidationStates(
	form: FormGroup,
	questions: FeedbackStructureQuestionFormQuestion[],
	controlNamePrefix: string,
	requiredCommentValidator: ValidatorFn,
	getControlValue: FeedbackAttemptControlValueGetter
): void {
	questions.forEach((question) => {
		updateCommentControlValidation(
			form,
			feedbackCommentControlName(question, controlNamePrefix),
			isQuestionCommentRequired(question),
			requiredCommentValidator
		);

		question.options
			.filter((option) => option.commentable === true)
			.forEach((option) => {
				updateCommentControlValidation(
					form,
					feedbackOptionCommentControlName(question, option, controlNamePrefix),
					isOptionCommentRequired(question, option, controlNamePrefix, getControlValue),
					requiredCommentValidator
				);
			});
	});
}

export function updateAnswerValidationStates(
	form: FormGroup,
	questions: FeedbackStructureQuestionFormQuestion[],
	controlNamePrefix: string
): void {
	questions.forEach((question) => {
		if (!questionUsesSingleAnswerControl(question)) {
			return;
		}

		updateAnswerControlValidation(
			form,
			feedbackAnswerControlName(question, controlNamePrefix),
			question,
			question.required
		);
	});
}

export function questionCanBeAutosaved(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter
): boolean {
	const answerRequest = createAnswerRequest(question, controlNamePrefix, getControlValue);
	return answerRequest.state === FeedbackAnswerState.ANSWERED
		&& requiredCommentsFilled(question, controlNamePrefix, getControlValue);
}

export function requiredCommentsFilled(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter
): boolean {
	if (isQuestionCommentRequired(question) && questionCommentValue(question, controlNamePrefix, getControlValue) === null) {
		return false;
	}

	return question.options.every((option) =>
		!isOptionCommentRequired(question, option, controlNamePrefix, getControlValue)
		|| optionCommentValueFromForm(question, option, controlNamePrefix, getControlValue) !== null
	);
}

export function isQuestionCommentRequired(question: FeedbackStructureQuestionFormQuestion): boolean {
	return hasQuestionCommentConfiguration(question) && question.configuration.commentable === true;
}

export function isOptionCommentRequired(
	question: FeedbackStructureQuestionFormQuestion,
	option: FeedbackStructureQuestionOption,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter
): boolean {
	if (option.commentable !== true) {
		return false;
	}

	switch (question.type) {
		case FeedbackQuestionType.SINGLE_SELECTION:
		case FeedbackQuestionType.DROPDOWN:
			return getControlValue(feedbackAnswerControlName(question, controlNamePrefix)) === option.position;
		case FeedbackQuestionType.MULTI_SELECTION:
			return getControlValue(feedbackOptionControlName(question, option, controlNamePrefix)) === true;
		case FeedbackQuestionType.ORDERING:
			return true;
		default:
			return false;
	}
}

export function getValidationErrorQuestionIds(
	displayedQuestions: FeedbackStructureQuestionFormQuestion[],
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	shouldSkipValidation: (questionId: number | undefined) => boolean,
	isQuestionLocked: (questionId: number | undefined) => boolean
): number[] {
	return Array.from(new Set([
		...getMissingRequiredAnswerQuestionIds(
			displayedQuestions,
			controlNamePrefix,
			getControlValue,
			shouldSkipValidation,
			isQuestionLocked
		),
		...getInvalidCommentQuestionIds(
			displayedQuestions,
			controlNamePrefix,
			getControlValue,
			shouldSkipValidation,
			isQuestionLocked
		),
	]));
}

export function hasMissingRequiredAnswer(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	shouldSkipValidation: (questionId: number | undefined) => boolean,
	isQuestionLocked: (questionId: number | undefined) => boolean
): boolean {
	return question.required
		&& !shouldSkipValidation(question.id)
		&& !isQuestionLocked(question.id)
		&& !hasQuestionAnswer(question, controlNamePrefix, getControlValue);
}

export function hasMissingRequiredComment(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	shouldSkipValidation: (questionId: number | undefined) => boolean,
	isQuestionLocked: (questionId: number | undefined) => boolean
): boolean {
	if (shouldSkipValidation(question.id) || isQuestionLocked(question.id)) {
		return false;
	}

	return hasQuestionAnswer(question, controlNamePrefix, getControlValue)
		&& !requiredCommentsFilled(question, controlNamePrefix, getControlValue);
}

export function hasQuestionAnswer(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter
): boolean {
	switch (question.type) {
		case FeedbackQuestionType.SINGLE_SELECTION:
		case FeedbackQuestionType.DROPDOWN:
		case FeedbackQuestionType.SLIDER:
		case FeedbackQuestionType.RATING:
		case FeedbackQuestionType.TEXT_INPUT:
		case FeedbackQuestionType.DATE_TIME:
		case FeedbackQuestionType.TIME:
		case FeedbackQuestionType.DATE:
		case FeedbackQuestionType.FILES:
			return hasSingleControlAnswer(question, getControlValue(feedbackAnswerControlName(question, controlNamePrefix)));
		case FeedbackQuestionType.MULTI_SELECTION:
			return question.options.some((option) => getControlValue(feedbackOptionControlName(question, option, controlNamePrefix)) === true);
		case FeedbackQuestionType.ORDERING:
			return question.options.length > 0;
		default:
			return false;
	}
}

function optionCommentValueFromForm(
	question: FeedbackStructureQuestionFormQuestion,
	option: FeedbackStructureQuestionOption,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter
): string | null {
	if (option.commentable !== true) {
		return null;
	}

	return normalizeTextValue(getControlValue(feedbackOptionCommentControlName(question, option, controlNamePrefix)));
}

function updateCommentControlValidation(
	form: FormGroup,
	controlName: string,
	required: boolean,
	requiredCommentValidator: ValidatorFn
): void {
	const control = form.get(controlName);
	if (control === null) {
		return;
	}

	if (required) {
		control.setValidators(requiredCommentValidator);
		control.updateValueAndValidity({emitEvent: false});
		if (normalizeTextValue(control.value) === null) {
			control.markAsDirty({onlySelf: true});
		}
		return;
	}

	control.clearValidators();
	control.updateValueAndValidity({emitEvent: false});
}

function updateAnswerControlValidation(
	form: FormGroup,
	controlName: string,
	question: FeedbackStructureQuestionFormQuestion,
	required: boolean
): void {
	const control = form.get(controlName);
	if (control === null) {
		return;
	}

	if (required) {
		control.setValidators(createRequiredAnswerValidator(question));
		control.updateValueAndValidity({emitEvent: false});
		if (control.invalid) {
			control.markAsDirty({onlySelf: true});
		}
		return;
	}

	control.clearValidators();
	control.updateValueAndValidity({emitEvent: false});
}

function getMissingRequiredAnswerQuestionIds(
	displayedQuestions: FeedbackStructureQuestionFormQuestion[],
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	shouldSkipValidation: (questionId: number | undefined) => boolean,
	isQuestionLocked: (questionId: number | undefined) => boolean
): number[] {
	return displayedQuestions
		.filter((question) =>
			hasMissingRequiredAnswer(
				question,
				controlNamePrefix,
				getControlValue,
				shouldSkipValidation,
				isQuestionLocked
			)
		)
		.map((question) => question.id)
		.filter((questionId): questionId is number => questionId !== undefined);
}

function getInvalidCommentQuestionIds(
	displayedQuestions: FeedbackStructureQuestionFormQuestion[],
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	shouldSkipValidation: (questionId: number | undefined) => boolean,
	isQuestionLocked: (questionId: number | undefined) => boolean
): number[] {
	return displayedQuestions
		.filter((question) =>
			hasMissingRequiredComment(
				question,
				controlNamePrefix,
				getControlValue,
				shouldSkipValidation,
				isQuestionLocked
			)
		)
		.map((question) => question.id)
		.filter((questionId): questionId is number => questionId !== undefined);
}

function questionUsesSingleAnswerControl(question: FeedbackStructureQuestionFormQuestion): boolean {
	switch (question.type) {
		case FeedbackQuestionType.SINGLE_SELECTION:
		case FeedbackQuestionType.DROPDOWN:
		case FeedbackQuestionType.SLIDER:
		case FeedbackQuestionType.RATING:
		case FeedbackQuestionType.TEXT_INPUT:
		case FeedbackQuestionType.DATE_TIME:
		case FeedbackQuestionType.TIME:
		case FeedbackQuestionType.DATE:
		case FeedbackQuestionType.FILES:
			return true;
		case FeedbackQuestionType.MULTI_SELECTION:
		case FeedbackQuestionType.ORDERING:
			return false;
	}
}

function createRequiredAnswerValidator(question: FeedbackStructureQuestionFormQuestion): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => (
		hasSingleControlAnswer(question, control.value) ? null : {required: true}
	);
}

function hasSingleControlAnswer(question: FeedbackStructureQuestionFormQuestion, value: unknown): boolean {
	switch (question.type) {
		case FeedbackQuestionType.SINGLE_SELECTION:
		case FeedbackQuestionType.DROPDOWN:
			return value !== null && value !== undefined && String(value).trim() !== '';
		case FeedbackQuestionType.RATING:
			return Number(value) > 0;
		case FeedbackQuestionType.FILES:
			return Array.isArray(value) && value.filter((file) => file !== null && file !== undefined).length > 0;
		case FeedbackQuestionType.SLIDER:
		case FeedbackQuestionType.TEXT_INPUT:
		case FeedbackQuestionType.DATE_TIME:
		case FeedbackQuestionType.TIME:
		case FeedbackQuestionType.DATE:
			return normalizedAnswerValue(question, value) !== null;
		case FeedbackQuestionType.MULTI_SELECTION:
		case FeedbackQuestionType.ORDERING:
			return false;
	}
}
